"use client";

import type { ExternalClientApp } from "@mlx/contracts";
import {
  BracesIcon,
  KeyRoundIcon,
  MonitorIcon,
  TerminalIcon,
} from "lucide-react";
import { useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  clientIcon,
  sourceIcon,
} from "@/components/content/icons/source-icons";
import { cn } from "@/lib/utils";
import { TwinLiveMark } from "@/components/app/twin/twin-live-mark";

export type ProvenanceActor = {
  label: string;
  imageUrl: string | null;
  initials: string;
  icon?: "twin" | "key";
};
export type ProvenanceInterface =
  | "web"
  | "cli"
  | "api"
  | "mcp"
  | "ios"
  | "slack"
  | "teams"
  | "copilot";

const PROVENANCE_INTERFACE_TYPES: ReadonlySet<string> = new Set([
  "web",
  "cli",
  "api",
  "mcp",
  "ios",
  "slack",
  "teams",
  "copilot",
] satisfies ProvenanceInterface[]);

/** Narrow an untyped source value to a known provenance interface — the one
 *  allow-list every surface shares, so new surfaces light up everywhere. */
export function provenanceInterfaceType(
  value: unknown,
): ProvenanceInterface | undefined {
  return typeof value === "string" && PROVENANCE_INTERFACE_TYPES.has(value)
    ? (value as ProvenanceInterface)
    : undefined;
}

/** The kind of Slack conversation a session was started in. */
export type ProvenanceConversation = "personal" | "group" | "channel";

export function ProvenanceAvatarStack(props: {
  actor?: ProvenanceActor | null;
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  conversation?: ProvenanceConversation | null;
  usedTwin?: boolean;
  executedByTwin?: boolean;
  action: "started" | "created" | "updated" | "posted" | "retrieved";
  size?: "sm" | "md";
  layout?: "stack" | "overlay";
  /** The Twin is still working on this record: its mark breathes. */
  working?: boolean;
  /** Breathe for this long after mount, or again when `restartKey` changes. */
  workingSeconds?: number;
  restartKey?: string | number;
  className?: string;
}) {
  const sourceVisible = Boolean(props.clientApp || props.interfaceType);
  // When the actor IS the twin its disc already carries the identity —
  // badging a second twin mark onto it double-marks and mangles the avatar.
  const twinVisible =
    Boolean(props.usedTwin || props.executedByTwin) &&
    props.actor?.icon !== "twin";
  if (!props.actor && !sourceVisible && !twinVisible) return null;
  const description = provenanceDescription(props);
  const sizeClass = props.size === "md" ? "size-8" : "size-5";
  const iconClass = props.size === "md" ? "size-4" : "size-3";
  // A corner badge scales with its avatar, so a small one keeps the face clear.
  const badgeClass = props.size === "md" ? "size-4 ring-1" : "size-3 ring-1";
  const overlay =
    props.layout === "overlay" &&
    Boolean(props.actor) &&
    (sourceVisible || twinVisible);
  const actorMark = props.actor ? (
    <ActorMark
      actor={props.actor}
      className={sizeClass}
      working={props.working}
      workingSeconds={props.workingSeconds}
      restartKey={props.restartKey}
    />
  ) : null;
  return (
    <TooltipProvider delay={180}>
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              role="img"
              aria-label={description}
              className={cn(
                "inline-flex items-center",
                overlay ? "relative" : "-space-x-1.5",
                props.className,
              )}
              tabIndex={0}
            />
          }
        >
          {overlay ? actorMark : null}
          {overlay ? (
            <span
              data-slot="provenance-badges"
              className={
                props.size === "md"
                  ? "absolute right-0 -top-1 flex flex-col -space-y-1"
                  : "absolute -right-1 -top-1 flex flex-col -space-y-1.5"
              }
              aria-hidden
            >
              {twinVisible ? (
                <TwinProvenanceMark
                  className={badgeClass}
                  working={props.working}
                  workingSeconds={props.workingSeconds}
                  restartKey={props.restartKey}
                />
              ) : null}
              {sourceVisible ? (
                <SourceMark
                  clientApp={props.clientApp}
                  interfaceType={props.interfaceType}
                  className={badgeClass}
                  iconClassName={props.size === "md" ? "size-2.5" : "size-2"}
                />
              ) : null}
            </span>
          ) : (
            <>
              {actorMark}
              {sourceVisible ? (
                <SourceMark
                  clientApp={props.clientApp}
                  interfaceType={props.interfaceType}
                  className={sizeClass}
                  iconClassName={iconClass}
                />
              ) : null}
              {twinVisible ? (
                // The Twin mark nestles into the stack as itself, the way the
                // thinking line shows it, rather than plated inside a disc.
                <TwinLiveMark
                  active={props.working}
                  className={cn(sizeClass, "relative")}
                  durationSeconds={props.workingSeconds}
                  restartKey={props.restartKey}
                />
              ) : null}
            </>
          )}
        </TooltipTrigger>
        <TooltipContent sideOffset={6}>{description}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const markClass =
  "relative flex shrink-0 items-center justify-center rounded-full border border-border/70 bg-background ring-2 ring-background";

function SourceMark(props: {
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  className: string;
  iconClassName: string;
}) {
  return (
    <span
      className={cn(
        markClass,
        "overflow-hidden text-foreground",
        props.className,
      )}
      aria-hidden
    >
      <SourceIcon
        clientApp={props.clientApp}
        interfaceType={props.interfaceType}
        className={sourceIconClassName(props.clientApp, props.iconClassName)}
      />
    </span>
  );
}

type TwinWorking = {
  working?: boolean;
  workingSeconds?: number;
  restartKey?: string | number;
};

function TwinProvenanceMark(props: TwinWorking & { className: string }) {
  // The disc IS the avatar: full-bleed rings fill the circle instead of
  // floating on the bg-background plate the other marks need. While the Twin
  // is working the same disc breathes instead of standing still.
  return (
    <span
      className={cn(markClass, "overflow-hidden", props.className)}
      aria-hidden
    >
      <TwinLiveMark
        bleed
        active={props.working}
        className="size-full"
        durationSeconds={props.workingSeconds}
        restartKey={props.restartKey}
      />
    </span>
  );
}

export function ActorMark({
  actor,
  className,
  working = false,
  workingSeconds,
  restartKey,
}: TwinWorking & {
  actor: ProvenanceActor;
  className: string;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = Boolean(actor.imageUrl && actor.imageUrl !== failedUrl);
  return (
    <span
      className={cn(
        markClass,
        "overflow-hidden bg-muted text-3xs font-semibold text-muted-foreground",
        className,
      )}
      aria-hidden
    >
      {actor.icon === "twin" ? (
        <TwinLiveMark
          bleed
          active={working}
          className="size-full"
          durationSeconds={workingSeconds}
          restartKey={restartKey}
        />
      ) : actor.icon === "key" ? (
        <KeyRoundIcon className="size-[62%]" strokeWidth={2} />
      ) : showImage ? (
        <img
          src={actor.imageUrl ?? undefined}
          alt=""
          className="size-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setFailedUrl(actor.imageUrl)}
        />
      ) : (
        actor.initials
      )}
    </span>
  );
}

function sourceIconClassName(
  clientApp: ExternalClientApp | null | undefined,
  className: string,
) {
  // One-colour marks (Codex) invert on dark; colour marks keep their colours.
  const client = clientApp ? clientIcon(clientApp) : null;
  return cn(className, client?.monochrome && "dark:invert");
}

function SourceIcon(props: {
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  className: string;
}) {
  const client = props.clientApp ? clientIcon(props.clientApp) : null;
  if (client) {
    return <img alt="" src={client.src} className={props.className} />;
  }
  // Messaging surfaces carry their brand marks like client apps do; the
  // one-colour Copilot mark inverts on dark like the monochrome brands.
  const surface =
    props.interfaceType === "slack" ||
    props.interfaceType === "teams" ||
    props.interfaceType === "copilot"
      ? sourceIcon(props.interfaceType)
      : null;
  if (surface) {
    return (
      <img
        alt=""
        src={surface.src}
        className={cn(props.className, surface.monochrome && "dark:invert")}
      />
    );
  }
  if (props.interfaceType === "cli")
    return <TerminalIcon className={props.className} />;
  // MCP has its own mark, the one the marketing site shows for it too.
  if (props.interfaceType === "mcp") {
    const mcp = sourceIcon("mcp")!;
    return <img alt="" src={mcp.src} className={cn(props.className, "dark:invert")} />;
  }
  if (props.interfaceType === "api")
    return <BracesIcon className={props.className} />;
  return <MonitorIcon className={props.className} />;
}

function provenanceDescription(props: {
  actor?: ProvenanceActor | null;
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  conversation?: ProvenanceConversation | null;
  usedTwin?: boolean;
  executedByTwin?: boolean;
  action: string;
}) {
  const actor = props.actor ? props.actor.label : "Twin";
  const app = clientAppLabel(props.clientApp);
  const via = props.interfaceType ? interfaceLabel(props.interfaceType) : null;
  // Slack's scope words are Slack's; Teams records the same field with its own meaning.
  const where =
    props.interfaceType === "slack" && props.conversation
      ? ` in ${slackConversationLabel(props.conversation)}`
      : "";
  const source = app
    ? ` using ${app}${via ? ` via ${via}` : ""}`
    : via
      ? ` via ${via}${where}`
      : "";
  const twin = props.executedByTwin
    ? ". The Twin executed the action"
    : props.usedTwin
      ? " with governed data from the Twin"
      : "";
  return `${actor} ${props.action} this${source}${twin}.`;
}

export function clientAppLabel(value?: ExternalClientApp | null) {
  if (value === "claude-code") return "Claude Code";
  if (value === "codex") return "Codex";
  if (value === "gemini") return "Gemini";
  if (value === "chatgpt") return "ChatGPT";
  return null;
}

export function interfaceLabel(value: ProvenanceInterface) {
  if (value === "cli") return "CLI";
  if (value === "mcp") return "MCP";
  if (value === "api") return "API";
  if (value === "ios") return "iOS";
  if (value === "slack") return "Slack";
  if (value === "teams") return "Microsoft Teams";
  if (value === "copilot") return "Copilot";
  return "web app";
}

/**
 * How provenance names the Slack conversation a session was started in. A
 * mention used to be recorded as one in a channel whatever it came from, so
 * "a channel" is as much as an older session's record supports.
 */
function slackConversationLabel(value: ProvenanceConversation) {
  if (value === "personal") return "a direct message";
  if (value === "group") return "a private channel or group message";
  return "a channel";
}

export function sessionClientProvenance(
  metadata: Record<string, unknown> | undefined,
): {
  clientApp?: ExternalClientApp;
  interfaceType?: ProvenanceInterface;
  conversation?: ProvenanceConversation;
} {
  // External-conversation sessions (Slack, Teams, Copilot) record their
  // origin as a top-level metadata.source rather than a client object.
  const externalSource = metadata?.source;
  const scope = metadata?.scope;
  const external =
    externalSource === "slack" ||
    externalSource === "teams" ||
    externalSource === "copilot"
      ? {
          interfaceType: externalSource as ProvenanceInterface,
          ...(externalSource === "slack" &&
          (scope === "personal" || scope === "group" || scope === "channel")
            ? { conversation: scope as ProvenanceConversation }
            : {}),
        }
      : {};
  const client = metadata?.client;
  if (!client || typeof client !== "object" || Array.isArray(client))
    return external;
  const record = client as Record<string, unknown>;
  const app = record.app;
  const source = record.source;
  return {
    ...external,
    ...(app === "claude-code" ||
    app === "codex" ||
    app === "gemini" ||
    app === "chatgpt"
      ? { clientApp: app as ExternalClientApp }
      : {}),
    ...(source === "web" ||
    source === "cli" ||
    source === "api" ||
    source === "mcp" ||
    source === "ios"
      ? { interfaceType: source as ProvenanceInterface }
      : {}),
  };
}

export function channelClientProvenance(
  metadata: Record<string, unknown> | undefined,
) {
  const value = metadata?.mlxProvenance;
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  const app = record.clientApp;
  const source = record.interfaceType;
  return {
    ...(app === "claude-code" ||
    app === "codex" ||
    app === "gemini" ||
    app === "chatgpt"
      ? { clientApp: app as ExternalClientApp }
      : {}),
    ...(source === "web" ? { interfaceType: "web" as const } : {}),
  };
}
